// components/dashboard/candidate/profile/CvUploadCard.tsx
// #47a — candidate CV upload. A self-contained profile section: the candidate
// uploads a CV (PDF/Word) that an employer can read on the application (F2-1
// ApplicantProfileModal already lists a candidate's profile documents). Uploads
// immediately via the candidate-profile document store (document_type="cv"), like
// the avatar uploader — NOT part of the profile-save form.
import React, { useRef, useState } from 'react';
import { FileText, Upload, Eye, Trash2, Loader2 } from 'lucide-react';
import { useCandidateProfile } from '@/contexts/CandidateProfileContext';
import { useToast } from '@/hooks/use-toast';
import { IconChip } from '@/components/custom-ui/IconChip';
import { PdfViewerModal } from '@/components/custom-ui/PdfViewerModal';
import { ConfirmDialog } from '@/components/custom-ui/ConfirmDialog';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

// PDF-only (#47a): keeps the CV easy for Link to read (the framework's PDF text
// extractor) and lets us preview it inline with the shared PdfViewerModal.
const ACCEPT = 'application/pdf,.pdf';
// CVs get a tighter cap than generic documents (5 MB vs the server's 10 MB
// default): candidates far outnumber employers, so per-candidate CV storage is
// what scales — and a CV should never need 10 MB. Mirrors the server-side
// CV-specific limit in document_service.go.
const MAX_MB = 5;

const isPdf = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

export const CvUploadCard: React.FC = () => {
    const { profileDocuments, uploadDocument, deleteDocument, getDocumentDownloadUrl, isUploadingDocument } =
        useCandidateProfile();
    const { toast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
    const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    const cvDocs = profileDocuments.filter((d) => d.document_type === 'cv');

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file after an error
        if (!file) return;
        if (!isPdf(file)) {
            toast({ title: 'Fel filformat', description: 'Ladda upp din CV som en PDF-fil.', variant: 'destructive' });
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast({ title: 'Filen är för stor', description: `Max ${MAX_MB} MB.`, variant: 'destructive' });
            return;
        }
        try {
            await uploadDocument('cv', file);
            toast({ title: 'CV uppladdat', description: 'Din CV är nu kopplad till din profil.' });
        } catch {
            toast({ title: 'Kunde inte ladda upp CV', description: 'Försök igen.', variant: 'destructive' });
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await deleteDocument(pendingDelete.id);
            toast({ title: 'CV borttaget' });
            setPendingDelete(null);
        } catch {
            toast({ title: 'Kunde inte ta bort CV', variant: 'destructive' });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
            <div className="flex items-center gap-3 mb-6">
                <IconChip icon={FileText} size="lg" />
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">CV</h2>
                    <p className="text-sm text-gray-500">
                        Ladda upp din CV så att arbetsgivare kan läsa den när du söker jobb.
                    </p>
                </div>
            </div>

            <div className="space-y-3">
                {cvDocs.map((doc) => (
                    <div
                        key={doc.id}
                        className="flex items-center justify-between gap-2 rounded-xl border-2 border-gray-100 bg-gray-50 p-3"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-[#1A4D4F] flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-sm text-gray-800 truncate">{doc.original_name || 'CV'}</p>
                                {doc.created_at && (
                                    <p className="text-[11px] text-gray-400">
                                        Uppladdad {new Date(doc.created_at).toLocaleDateString('sv-SE')}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() =>
                                    setPreview({ url: getDocumentDownloadUrl(doc.id), title: doc.original_name || 'CV' })
                                }
                                className="inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:underline"
                            >
                                <Eye className="w-3.5 h-3.5" /> Visa
                            </button>
                            <button
                                type="button"
                                onClick={() => setPendingDelete({ id: doc.id, name: doc.original_name || 'CV' })}
                                className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Ta bort
                            </button>
                        </div>
                    </div>
                ))}

                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={isUploadingDocument}
                    className="w-full h-24 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#1A4D4F] hover:bg-[#A4C3B2]/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {isUploadingDocument ? (
                        <>
                            <Loader2 className="w-5 h-5 text-[#1A4D4F] animate-spin" />
                            <span className="text-xs text-gray-500">Laddar upp…</span>
                        </>
                    ) : (
                        <>
                            <Upload className="w-5 h-5 text-gray-400" />
                            <span className="text-xs text-gray-500">
                                {cvDocs.length ? 'Ersätt din CV' : 'Klicka för att ladda upp din CV'}
                            </span>
                            <span className="text-[11px] text-gray-400">PDF · max {MAX_MB} MB</span>
                        </>
                    )}
                </button>
                <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleFile} className="hidden" />
            </div>

            {preview && (
                <PdfViewerModal url={preview.url} title={preview.title} onClose={() => setPreview(null)} />
            )}

            <ConfirmDialog
                open={!!pendingDelete}
                onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
                destructive
                title="Ta bort CV"
                description={
                    <>Är du säker på att du vill ta bort <strong>{pendingDelete?.name}</strong>? Detta går inte att ångra.</>
                }
                confirmLabel="Ta bort"
                onConfirm={confirmDelete}
                isBusy={deleting}
            />
        </div>
    );
};

export default CvUploadCard;
